import { QueryFunctionContext, useInfiniteQuery, useQueries } from 'react-query';

import { Configuration } from '@custom-types/Configuration';

import {
  list as listFn,
  getConfiguration as getConfigurationFn,
  ConfigurationData,
  get as getFn,
  listForStaff as listForStaffFn,
  Field,
} from './service';

interface EventsArguments {
  searchValue?: string;
}
type EventsKey = ['events', EventsArguments];

const eventsQuery = {
  getQueryKey: (param: EventsArguments): EventsKey => ['events', param],
  queryFn: ({ pageParam, queryKey: [, param] }: QueryFunctionContext<EventsKey>) =>
    listFn(pageParam || 0, param.searchValue),
};

export const useEvents = (searchValue: string) =>
  useInfiniteQuery({
    queryKey: eventsQuery.getQueryKey({ searchValue }),
    queryFn: eventsQuery.queryFn,
  });

type EventKey = ['event', string];
const eventQuery = {
  getQueryKey: (eventId: string): EventKey => ['event', eventId],
  queryFn: ({ queryKey: [, eventId] }: QueryFunctionContext<EventKey>) => getFn(eventId),
};

type ConfigurationKey = ['configuration', string];
const configurationQuery = {
  getQueryKey: (eventId: string): ConfigurationKey => ['configuration', eventId],
  queryFn: ({ queryKey: [, eventId] }: QueryFunctionContext<ConfigurationKey>) =>
    getConfigurationFn(eventId),
};

export const useEventAndConfiguration = (eventId: string) =>
  useQueries({
    queries: [
      {
        queryKey: eventQuery.getQueryKey(eventId),
        queryFn: eventQuery.queryFn,
      },
      {
        queryKey: configurationQuery.getQueryKey(eventId),
        queryFn: configurationQuery.queryFn,
        select: (settings: ConfigurationData): Configuration => ({
          kioskModeEnabled: settings.configuration.checkinEnableKioskMode === '1',
          checkOutEnabled: settings.configuration.checkinEnableCheckout === '1',
          searchEmailEnabled: settings.configuration.checkinEnableSearchByNameOrEmail === '1',
          kioskColor: settings.configuration.checkinKioskColor,
          showPeopleGroups: settings.configuration.checkinShowPeopleGroups === '1',
          badgePrintingOnsiteLabelsEnabled:
            settings.configuration.badgePrintingOnsiteLabelsEnabled === '1',
          badgePrintingImage: settings.badgePrintingImage,
          idleScreenImage: settings.idleScreenImage,
          onsiteLabelPrintingBadges: settings.onsiteLabelPrintingBadges,
        }),
      },
    ],
  });

interface EventsForStaffArguments {
  [Field.Id]?: string;
  [Field.Code]?: string;
}
type EventsForStaffKey = ['eventsForStaff', EventsForStaffArguments];

const eventsForStaffQuery = {
  getQueryKey: (param: EventsForStaffArguments): EventsForStaffKey => ['eventsForStaff', param],
  queryFn: async ({
    pageParam: page,
    queryKey: [, param],
  }: QueryFunctionContext<EventsForStaffKey>) => listForStaffFn({ ...param, page }),
};

export const useEventsForStaff = (args: EventsForStaffArguments) =>
  useInfiniteQuery({
    queryKey: eventsForStaffQuery.getQueryKey(args),
    queryFn: eventsForStaffQuery.queryFn,
    enabled: !!(args[Field.Code] || args[Field.Id]),
  });
